import { Typography, Box, CircularProgress, Link, useMediaQuery } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { likeProduct, dislikeProduct, fetchProductList, fetchProductListCandidates } from './listService';
import SwipeCard from './SwipeCard';
import SwipeActionButton from './SwipeActionButton';
import ZoomModal from './ZoomModal';

function Swipe() {
    const [likeCount, setLikeCount] = useState(0);
    const { productListId } = useParams();
    const [candidateIndex, setCandidateIndex] = useState(0);
    const [zoomOpen, setZoomOpen] = useState(false);
    const [zoomProductIndex, setZoomProductIndex] = useState(0);
    const [zoomImageIndex, setZoomImageIndex] = useState(0);

    const isMobile = useMediaQuery('(max-width:600px)');

    const { isLoading2, error2, data: candidates } = useQuery({
        queryKey: ['lists', productListId, 'candidates'],
        queryFn: () => fetchProductListCandidates(productListId),
        refetchOnWindowFocus: false,
        refetchOnMount: true,
    });

    const likeProductMutation = useMutation({
        mutationFn: ({ productId, source }) => likeProduct(productListId, productId, source),
        onSuccess: () => {
            setLikeCount((prevCount) => prevCount + 1);
        },
        onError: (error) => {
            console.error('Error liking product:', error);
        },
    });

    const dislikeMutation = useMutation({
        mutationFn: ({ productId, source }) => dislikeProduct(productListId, productId, source),
        onSuccess: () => {},
        onError: (error) => {
            console.error('Error disliking product:', error);
        },
    });

    const handleLike = (source) => {
        const product = candidates[candidateIndex];
        if (!product) return;

        likeProductMutation.mutate({ productId: product.id, source });

        setCandidateIndex((prevIndex) => prevIndex + 1);
    };

    const handleDislike = (source) => {
        const product = candidates[candidateIndex];
        if (!product) return;

        dislikeMutation.mutate({ productId: product.id, source });

        setCandidateIndex((prevIndex) => prevIndex + 1);
    };
    
    const handleZoomClose = () => setZoomOpen(false);

    const handleZoomImage = (productIndex, imageIndex) => {
        setZoomProductIndex(productIndex);
        setZoomImageIndex(imageIndex);
        setZoomOpen(true);
    }

    const { isLoading, error, data: productList } = useQuery({
        queryKey: ['lists', productListId],
        queryFn: () => fetchProductList(productListId),
    });

    if (isLoading || isLoading2) return <CircularProgress />;
    if (error || error2) {
        console.error('Failed to fetch product list:', error);
        return <Typography variant="h6">Hiba történt a terméklista betöltése közben</Typography>;
    }

    if (!candidates) return 'Nincsenek termékek';

    return (
        <>
            <Box position="relative">
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {candidateIndex >= candidates.length && (
                        <Box sx={{ minHeight: '50vh' }}>
                            <Typography variant="h6">Nincs több termék</Typography>
                        </Box>
                    )}
                    {candidateIndex < candidates.length && (
                        <Box
                            display="flex"
                            alignItems="center"
                            flexDirection={isMobile ? 'column' : 'row'}
                        >
                            {!isMobile && (
                                <Box sx={{ p: 2 }} maxWidth={150}>
                                    <Typography sx={{fontWeight: 600}}>
                                        Ha nem tetszik húzd balra, vagy nyomj egy "X"-et
                                    </Typography>
                                    <SwipeActionButton actionType="dislike" onClick={() => handleDislike("button")} />
                                </Box>
                            )}
                            <Box alignItems="center" justifyContent="center" textAlign="center">
                                <Typography variant="h5" mb={5}>
                                    Hátralévő termékek: {candidates.length - candidateIndex}
                                </Typography>
                                <Box
                                    sx={{
                                        zIndex: 1,
                                        width: '100%',
                                        height: '100%',
                                        position: 'relative',
                                    }}
                                >
                                    <SwipeCard
                                        onLike={() => handleLike("swipe")}
                                        onDislike={() => handleDislike("swipe")}
                                        product={candidates[candidateIndex]}
                                        onZoomImage={(imageIndex) => handleZoomImage(candidateIndex, imageIndex)}
                                    />
                                    {candidateIndex + 1 < candidates.length && (
                                        <Box
                                            position="absolute"
                                            top={-50}
                                            left="50%"
                                            sx={{
                                                zIndex: -1,
                                                transform: 'translateX(-50%) scale(0.9)',
                                                transition: 'transform 0.3s ease',
                                            }}
                                        >
                                            <SwipeCard product={candidates[candidateIndex + 1]} />
                                        </Box>
                                    )}
                                    {candidateIndex + 2 < candidates.length && (
                                        <Box
                                            position="absolute"
                                            top={-100}
                                            left="50%"
                                            sx={{
                                                zIndex: -2,
                                                transform: 'translateX(-50%) scale(0.8)',
                                                transition: 'transform 0.3s ease',
                                            }}
                                        >
                                            <SwipeCard product={candidates[candidateIndex + 2]} />
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                            {!isMobile && (
                                <Box sx={{ p: 2 }} maxWidth={150}>
                                    <Typography sx={{fontWeight:600}}>Ha tetszik húzd jobbra, vagy kattints a szívecskére</Typography>
                                    <SwipeActionButton actionType="like" onClick={() => handleLike("button")} />
                                </Box>
                            )}
                        </Box>
                    )}
                    {isMobile && candidateIndex < candidates.length && (
                        <Box display="flex" justifyContent="space-around" sx={{ width: '100%', mt: 2 }}>
                            <SwipeActionButton actionType="dislike" onClick={() => handleDislike("button")} />
                            <SwipeActionButton actionType="like" onClick={() => handleLike("button")} />
                        </Box>
                    )}
                </Box>
                <Box
                    {...(isMobile ? {} : { position: "absolute", right: 0, top: 0 })}
                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                    <Box
                        width={100}
                        height={100}
                        sx={{
                            bgcolor: 'primary.main',
                            justifyContent: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            borderRadius: '4px',
                        }}
                    >
                        <Typography variant="h2" color="white">
                            {likeCount}
                        </Typography>
                    </Box>
                    <Link component={RouterLink} to={`/lists/${productList.id}`}>
                        {productList.name}
                    </Link>
                </Box>
            </Box>
            <ZoomModal zoomOpen={zoomOpen} onZoomClose={handleZoomClose} imageSrc={candidates[zoomProductIndex].imageUrls[zoomImageIndex]} altText={candidates[zoomProductIndex].name} />
        </>
    );
}

export default Swipe;
